import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { CreditCard, CheckCircle2, MapPin, Truck, Store } from "lucide-react";
import { toast } from "sonner";
import api, { inr, formatError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";

const STEPS = ["Address", "Delivery", "Payment", "Review"];

export default function CheckoutPage() {
  const nav = useNavigate();
  const { refresh } = useCart();
  const [step, setStep] = useState(0);
  const [validation, setValidation] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [placed, setPlaced] = useState(null);
  const [address, setAddress] = useState({ name: "", phone: "", line1: "", city: "", state: "", pincode: "" });
  const [payMethod, setPayMethod] = useState("MOCK_CARD");

  useEffect(() => {
    api.post("/checkout/validate").then(({ data }) => setValidation(data)).catch((e) => {
      toast.error(formatError(e));
      nav("/cart");
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addressValid = address.name && address.phone && address.line1 && address.city && address.state && address.pincode;

  const placeOrder = async () => {
    setPlacing(true);
    try {
      await api.post("/payments/mock", { method: payMethod });
      const { data } = await api.post("/checkout/place", { address, payment_method: payMethod });
      setPlaced(data.order);
      refresh();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setPlacing(false);
    }
  };

  if (placed) {
    return (
      <div data-testid="order-confirmation" className="max-w-2xl mx-auto px-8 mt-20 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
        <h1 className="font-heading text-3xl font-bold mt-6">Order confirmed</h1>
        <p className="text-slate-500 mt-2">Order <span className="font-mono font-semibold" data-testid="confirm-order-id">{placed.id.slice(0, 8).toUpperCase()}</span> · {inr(placed.total)} · Payment {placed.payment.status}</p>
        <p className="text-sm text-slate-400 mt-1">Your items were split into separate seller shipments — track each one from your orders page.</p>
        <div className="flex gap-3 justify-center mt-8">
          <Button asChild className="rounded-full bg-slate-900"><Link to={`/orders/${placed.id}`} data-testid="track-order-btn">Track order</Link></Button>
          <Button asChild variant="outline" className="rounded-full"><Link to="/search">Keep shopping</Link></Button>
        </div>
      </div>
    );
  }

  if (!validation) return <div className="max-w-7xl mx-auto px-8 mt-12"><div className="h-96 rounded-2xl bg-slate-100 animate-pulse" /></div>;

  const bySeller = validation.lines.reduce((acc, l) => {
    (acc[l.seller_name] = acc[l.seller_name] || []).push(l);
    return acc;
  }, {});

  return (
    <div data-testid="checkout-page" className="max-w-5xl mx-auto px-4 sm:px-8 mt-10">
      <h1 className="font-heading text-3xl font-bold tracking-tight">Checkout</h1>
      <div className="flex gap-2 mt-6 mb-10">
        {STEPS.map((s, i) => (
          <button key={s} data-testid={`checkout-step-${s.toLowerCase()}`} onClick={() => i < step && setStep(i)}
            className={`flex-1 text-xs font-bold uppercase tracking-widest py-2.5 rounded-full transition-colors ${i === step ? "bg-slate-900 text-white" : i < step ? "bg-blue-50 text-blue-600" : "bg-slate-100 text-slate-400"}`}>
            {i + 1}. {s}
          </button>
        ))}
      </div>

      {step === 0 && (
        <div data-testid="address-step" className="max-w-xl">
          <p className="font-heading font-bold text-lg mb-6 flex items-center gap-2"><MapPin className="w-5 h-5 text-blue-500" /> Delivery address</p>
          <div className="grid grid-cols-2 gap-4">
            {[["name", "Full name"], ["phone", "Phone"], ["line1", "Address line"], ["city", "City"], ["state", "State"], ["pincode", "PIN code"]].map(([k, label]) => (
              <input key={k} data-testid={`address-${k}`} value={address[k]} onChange={(e) => setAddress({ ...address, [k]: e.target.value })}
                placeholder={label} className={`h-11 rounded-md border border-slate-200 px-4 text-sm outline-none focus:border-blue-500 ${k === "line1" ? "col-span-2" : ""}`} />
            ))}
          </div>
          <Button data-testid="address-continue-btn" disabled={!addressValid} onClick={() => setStep(1)} className="mt-6 rounded-full bg-slate-900 px-8">Continue to delivery</Button>
        </div>
      )}

      {step === 1 && (
        <div data-testid="delivery-step">
          <p className="font-heading font-bold text-lg mb-6 flex items-center gap-2"><Truck className="w-5 h-5 text-blue-500" /> Delivery options</p>
          <div className="space-y-4 max-w-2xl">
            {Object.entries(bySeller).map(([seller, lines]) => (
              <div key={seller} className="rounded-2xl border border-slate-200 p-6">
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 flex items-center gap-2"><Store className="w-3.5 h-3.5" /> Shipment from {seller}</p>
                {lines.map((l) => (
                  <div key={l.offer_id} className="flex justify-between items-center mt-3 text-sm">
                    <span className="text-slate-600">{l.title} × {l.qty}</span>
                    <span className="font-semibold">{l.delivery_days}-day delivery · {l.shipping_fee ? inr(l.shipping_fee) : "Free"}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
          <Button data-testid="delivery-continue-btn" onClick={() => setStep(2)} className="mt-6 rounded-full bg-slate-900 px-8">Continue to payment</Button>
        </div>
      )}

      {step === 2 && (
        <div data-testid="payment-step" className="max-w-xl">
          <p className="font-heading font-bold text-lg mb-6 flex items-center gap-2"><CreditCard className="w-5 h-5 text-blue-500" /> Payment</p>
          <RadioGroup value={payMethod} onValueChange={setPayMethod} className="space-y-3">
            {[["MOCK_CARD", "Card (simulated)", "Visa, Mastercard, RuPay — no real charge"], ["MOCK_UPI", "UPI (simulated)", "Pay via UPI — no real charge"]].map(([v, t, d]) => (
              <Label key={v} htmlFor={v} data-testid={`pay-method-${v.toLowerCase()}`}
                className={`flex items-center gap-4 rounded-2xl border p-5 cursor-pointer transition-colors ${payMethod === v ? "border-blue-500 bg-blue-50/40" : "border-slate-200"}`}>
                <RadioGroupItem value={v} id={v} />
                <span><span className="font-semibold text-sm block">{t}</span><span className="text-xs text-slate-400">{d}</span></span>
              </Label>
            ))}
          </RadioGroup>
          <Button data-testid="payment-continue-btn" onClick={() => setStep(3)} className="mt-6 rounded-full bg-slate-900 px-8">Review order</Button>
        </div>
      )}

      {step === 3 && (
        <div data-testid="review-step" className="grid md:grid-cols-[1fr_320px] gap-10">
          <div>
            <p className="font-heading font-bold text-lg mb-4">Review your order</p>
            <div className="rounded-2xl border border-slate-200 p-6 space-y-3">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Deliver to</p>
              <p className="text-sm">{address.name} · {address.line1}, {address.city}, {address.state} {address.pincode}</p>
              <div className="border-t border-slate-100 pt-3 space-y-2">
                {validation.lines.map((l) => (
                  <div key={l.offer_id} className="flex justify-between text-sm">
                    <span className="text-slate-600">{l.title} × {l.qty} <span className="text-slate-400">({l.seller_name})</span></span>
                    <span className="font-semibold">{inr(l.price * l.qty)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <aside className="rounded-2xl border border-slate-200 p-6 h-fit">
            <div className="space-y-2.5 text-sm">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{inr(validation.subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Shipping</span><span className="font-semibold">{validation.shipping ? inr(validation.shipping) : "Free"}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tax (incl.)</span><span className="font-semibold">{inr(validation.tax)}</span></div>
              <div className="border-t border-slate-100 pt-3 flex justify-between font-heading font-bold text-lg"><span>Total</span><span data-testid="review-total">{inr(validation.total)}</span></div>
            </div>
            <Button data-testid="place-order-btn" onClick={placeOrder} disabled={placing} className="w-full mt-6 rounded-full h-12 bg-slate-900 hover:bg-slate-700 font-semibold">
              {placing ? "Placing order…" : "Place order"}
            </Button>
            <p className="text-[11px] text-slate-400 mt-3 text-center">Price, stock and seller status were re-validated just now.</p>
          </aside>
        </div>
      )}
    </div>
  );
}
